import { useEffect, useState } from 'react'
import {
  Button,
  Card,
  Checkbox,
  Group,
  Select,
  Switch,
  Textarea,
  TextInput,
  Title,
  Stack,
  Divider,
  ActionIcon,
  Alert,
  Accordion,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import {
  IconPlus,
  IconTrash,
  IconInfoCircle,
  IconStethoscope,
  IconMoodSmileBeam,
} from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { Parameters, ResourceType } from '@medplum/fhirtypes'
import { Navigation } from './components'
import { modals } from '@mantine/modals'
import { logError } from '@/utils/errorUtils'

interface ReminderConfig {
  id: string
  parameterId?: string // ID of the Parameter resource if it exists
  name: string
  subject: string
  resourceType: string
  condition: 'before' | 'after'
  timeValue: number
  timeUnit: 'days' | 'hours' | 'minutes'
  enableEmail: boolean
  enableSms: boolean
  sendToPractitioner: boolean
  sendToPatient: boolean
  emailTemplate: string
  smsTemplate: string
}

export function Reminders() {
  const medplum = useMedplum()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [deletingIndices, setDeletingIndices] = useState<Set<number>>(new Set())
  const [isInfo, setIsInfo] = useState(true)
  const [itemKey, setItemKey] = useState<string | null>('0')

  const form = useForm<{ reminders: ReminderConfig[] }>({
    initialValues: {
      reminders: [
        {
          id: crypto.randomUUID(),
          name: '',
          subject: '',
          resourceType: 'Appointment',
          condition: 'before',
          timeValue: 1,
          timeUnit: 'days',
          enableEmail: false,
          enableSms: false,
          sendToPractitioner: false,
          sendToPatient: false,
          emailTemplate: '',
          smsTemplate: '',
        },
      ],
    },
    validate: {
      reminders: {
        name: (value) => (!value?.trim() ? 'Reminder name is required' : null),
        timeValue: (value) =>
          value < 1 || value > 60 ? 'Time value must be between 1 and 23' : null,
        emailTemplate: (value, values, path) => {
          const index = parseInt(path.split('.')[1])
          const reminder = values.reminders[index]
          if (reminder?.enableEmail && !value.trim()) {
            return 'Email template is required when email notifications are enabled'
          }
          return null
        },
        smsTemplate: (value, values, path) => {
          const index = parseInt(path.split('.')[1])
          const reminder = values.reminders[index]
          if (reminder?.enableSms && !value.trim()) {
            return 'SMS template is required when SMS notifications are enabled'
          }
          return null
        },
      },
    },
  })

  useEffect(() => {
    if (localStorage.getItem('isInfo') === 'false') {
      setIsInfo(false)
    }

    const fetchExistingReminders = async () => {
      try {
        setIsLoadingData(true)

        const searchResult = await medplum.searchResources('Parameters', {
          _source: 'system-reminder',
        })

        if (searchResult.length > 0) {
          const existingReminders = searchResult.map((param) => {
            const getParameterValue = (name: string) => {
              const parameter = param.parameter?.find((p) => p.name === name)
              return parameter?.valueString || parameter?.valueBoolean || parameter?.valueInteger
            }

            return {
              id: crypto.randomUUID(), // Form internal ID
              parameterId: param.id, // Parameter resource ID for updates
              name: (getParameterValue('name') as string) || '',
              resourceType: (getParameterValue('resourceType') as string) || 'Appointment',
              subject: (getParameterValue('subject') as string) || '',
              condition: (getParameterValue('condition') as 'before' | 'after') || 'before',
              timeValue: (getParameterValue('timeValue') as number) || 1,
              timeUnit: (getParameterValue('timeUnit') as 'days' | 'hours' | 'minutes') || 'days',
              enableEmail: (getParameterValue('enableEmail') as boolean) || false,
              enableSms: (getParameterValue('enableSms') as boolean) || false,
              sendToPractitioner: (getParameterValue('sendToPractitioner') as boolean) || false,
              sendToPatient: (getParameterValue('sendToPatient') as boolean) || false,
              emailTemplate: (getParameterValue('emailTemplate') as string) || '',
              smsTemplate: (getParameterValue('smsTemplate') as string) || '',
            }
          })

          form.setValues({ reminders: existingReminders })
        }
      } catch (error) {
        logError('super', 'Failed to fetch existing reminders', error)
        notifications.show({
          title: 'Warning',
          message: 'Could not load existing reminders. Starting with default configuration.',
          color: 'yellow',
        })
      } finally {
        setIsLoadingData(false)
      }
    }

    fetchExistingReminders()
  }, [medplum])

  const addReminder = () => {
    form.insertListItem('reminders', {
      id: crypto.randomUUID(),
      name: '',
      resourceType: 'Appointment',
      subject: '',
      condition: 'before',
      timeValue: 1,
      timeUnit: 'days',
      enableEmail: false,
      enableSms: false,
      sendToPractitioner: false,
      sendToPatient: false,
      emailTemplate: '',
      smsTemplate: '',
    })
    const newKey = form.getValues().reminders.length - 1
    setItemKey(newKey.toString())
  }

  const removeReminder = async (index: number) => {
    const reminder = form.values.reminders[index]

    // Add to loading state
    setDeletingIndices((prev) => new Set(prev).add(index))

    try {
      // If this reminder has a parameterId, delete it from the backend
      if (reminder.parameterId) {
        await medplum.deleteResource('Parameters', reminder.parameterId)
        notifications.show({
          title: 'Success',
          message: `Reminder "${reminder.name || 'Untitled'}" deleted successfully`,
          color: 'green',
        })
      }

      // Remove from form
      form.removeListItem('reminders', index)
    } catch (error) {
      logError('super', 'Failed to delete reminder', error)
      notifications.show({
        title: 'Error',
        message: `Failed to delete reminder "${reminder.name || 'Untitled'}". Please try again.`,
        color: 'red',
      })
    } finally {
      // Remove from loading state
      setDeletingIndices((prev) => {
        const newSet = new Set(prev)
        newSet.delete(index)
        return newSet
      })
    }
  }

  const handleSubmit = async (values: { reminders: ReminderConfig[] }) => {
    setIsLoading(true)

    try {
      // Process each reminder (create new or update existing)
      const promises = values.reminders.map(async (reminder) => {
        const parameter: Parameters = {
          resourceType: 'Parameters',
          meta: {
            source: 'system-reminder',
          },
          parameter: [
            {
              name: 'name',
              valueString: reminder.name,
            },
            {
              name: 'resourceType',
              valueString: reminder.resourceType,
            },
            {
              name: 'condition',
              valueString: reminder.condition,
            },
            {
              name: 'timeValue',
              valueInteger: reminder.timeValue,
            },
            {
              name: 'timeUnit',
              valueString: reminder.timeUnit,
            },
            {
              name: 'enableEmail',
              valueBoolean: reminder.enableEmail,
            },
            {
              name: 'enableSms',
              valueBoolean: reminder.enableSms,
            },
            {
              name: 'sendToPractitioner',
              valueBoolean: reminder.sendToPractitioner,
            },
            {
              name: 'sendToPatient',
              valueBoolean: reminder.sendToPatient,
            },
            {
              name: 'emailTemplate',
              valueString: reminder.emailTemplate || '-',
            },
            {
              name: 'smsTemplate',
              valueString: reminder.smsTemplate || '-',
            },
            {
              name: 'subject',
              valueString: reminder.subject,
            },
          ],
        }

        // Update existing or create new based on parameterId
        if (reminder.parameterId) {
          parameter.id = reminder.parameterId
          return await medplum.updateResource(parameter)
        } else {
          return await medplum.createResource(parameter)
        }
      })

      const results = await Promise.all(promises)

      // Update form with new parameter IDs for newly created resources
      const updatedReminders = values.reminders.map((reminder, index) => ({
        ...reminder,
        parameterId: reminder.parameterId || results[index].id,
      }))

      form.setValues({ reminders: updatedReminders })

      notifications.show({
        title: 'Success',
        message: `${values.reminders.length} reminder${values.reminders.length > 1 ? 's' : ''} saved successfully`,
        color: 'green',
      })
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save reminders. Please try again.',
        color: 'red',
      })
      logError('super', 'Failed to save reminders', error)
    } finally {
      setIsLoading(false)
    }
  }

  const resourceTypeOptions = [{ value: 'Appointment', label: 'Appointment' }]

  const timeValueOptions = Array.from({ length: 23 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString(),
  }))
  const timeValueMinuteOptions = Array.from({ length: 60 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString(),
  }))

  const getTemplateForRender = (template: string) => {
    template = template.replace(
      /{{logoUrl}}/g,
      'https://behuman-static.s3.us-east-1.amazonaws.com/logo.png',
    )
    return template
  }

  const showPreview = (reminderIdx: number) => {
    modals.open({
      size: 'xl',
      children: (
        <div
          dangerouslySetInnerHTML={{
            __html: getTemplateForRender(form.getValues().reminders[reminderIdx].emailTemplate),
          }}
        />
      ),
    })
  }

  return (
    <>
      <Navigation />
      <div className="px-6 py-4">
        <Title order={2} mb="md">
          Reminders Settings
        </Title>

        {isInfo && (
          <Alert
            icon={<IconInfoCircle size="1rem" />}
            title="Information"
            className="mb-4 whitespace-pre-wrap"
            withCloseButton
            onClose={() => {
              setIsInfo(false)
              localStorage.setItem('isInfo', 'false')
            }}
          >
            {`Configure automatic reminders for appointments. Each reminder will be sent based on the
specified timing and conditions. You can use template variables in your notification messages:

General variables:
- {{logoUrl}} - URL of the logo image
- {{patient.name}} - Patient's full name
- {{practitioner.name}} - Practitioner's full name

Appointment specific variables:
- {{appointment.start}} - Appointment start time
- {{appointment.end}} - Appointment end time

Encounter specific variables:
- {{encounter.id}} - Encounter ID
- {{magicLink}} - Magic link for patient access`}
          </Alert>
        )}

        {!isInfo && (
          <Button
            className="mb-4 cursor-pointer"
            variant="light"
            onClick={() => {
              setIsInfo(true)
              localStorage.setItem('isInfo', 'true')
            }}
          >
            <IconInfoCircle size="1rem" />
          </Button>
        )}

        {isLoadingData ? (
          <Alert icon={<IconInfoCircle size="1rem" />} title="Loading" mb="lg">
            Loading existing reminder configurations...
          </Alert>
        ) : (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              <Accordion value={itemKey} onChange={setItemKey}>
                {form.values.reminders.map((reminder, index) => (
                  <Accordion.Item key={index} value={index.toString()}>
                    <Accordion.Control>
                      {reminder.name || `Reminder ${index + 1}`}
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Card key={reminder.id} withBorder p="md">
                        <Group justify="space-between" align="flex-start" mb="md">
                          <div>
                            <Title order={4}>{reminder.name || `Reminder ${index + 1}`}</Title>
                            <TextInput
                              label="Reminder Name"
                              placeholder="Enter reminder name"
                              {...form.getInputProps(`reminders.${index}.name`)}
                              required
                              error={form.errors[`reminders.${index}.name`]}
                            />
                          </div>
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => removeReminder(index)}
                            loading={deletingIndices.has(index)}
                            disabled={deletingIndices.has(index) || isLoading}
                          >
                            <IconTrash size="1rem" />
                          </ActionIcon>
                        </Group>

                        <Stack gap="sm">
                          <Group>
                            <Select
                              label="Resource Type"
                              placeholder="Select resource type"
                              data={resourceTypeOptions}
                              {...form.getInputProps(`reminders.${index}.resourceType`)}
                              required
                            />
                          </Group>

                          <Group>
                            <Checkbox
                              disabled={
                                !(['Appointment'] as ResourceType[]).includes(
                                  reminder.resourceType as ResourceType,
                                )
                              }
                              label={
                                <p className="flex gap-2">
                                  <IconStethoscope color="green" /> Send to pracitioner
                                </p>
                              }
                              {...form.getInputProps(`reminders.${index}.sendToPractitioner`, {
                                type: 'checkbox',
                              })}
                            />
                          </Group>
                          <Group>
                            <Checkbox
                              disabled={
                                !(['Appointment'] as ResourceType[]).includes(
                                  reminder.resourceType as ResourceType,
                                )
                              }
                              label={
                                <p className="flex gap-2">
                                  <IconMoodSmileBeam color="orange" /> Send to patient
                                </p>
                              }
                              {...form.getInputProps(`reminders.${index}.sendToPatient`, {
                                type: 'checkbox',
                              })}
                            />
                          </Group>

                          <Group>
                            <Switch
                              label={
                                form.values.reminders[index].condition === 'before'
                                  ? 'Before'
                                  : 'After'
                              }
                              checked={form.values.reminders[index].condition === 'before'}
                              onChange={(event) =>
                                form.setFieldValue(
                                  `reminders.${index}.condition`,
                                  event.currentTarget.checked ? 'before' : 'after',
                                )
                              }
                            />
                          </Group>

                          <Group>
                            <Select
                              label="Time Value"
                              placeholder="Select time value"
                              data={
                                form.values.reminders[index].timeUnit !== 'minutes'
                                  ? timeValueOptions
                                  : timeValueMinuteOptions
                              }
                              value={form.values.reminders[index].timeValue.toString()}
                              onChange={(value) =>
                                form.setFieldValue(
                                  `reminders.${index}.timeValue`,
                                  parseInt(value || '1'),
                                )
                              }
                              error={form.errors[`reminders.${index}.timeValue`]}
                              required
                            />

                            <Select
                              label="Time Unit"
                              placeholder="Select time unit"
                              data={[
                                { value: 'days', label: 'Days' },
                                { value: 'hours', label: 'Hours' },
                                { value: 'minutes', label: 'Minutes' },
                              ]}
                              {...form.getInputProps(`reminders.${index}.timeUnit`)}
                              required
                            />
                          </Group>

                          <Group>
                            <Checkbox
                              label="Email Notifications"
                              {...form.getInputProps(`reminders.${index}.enableEmail`, {
                                type: 'checkbox',
                              })}
                            />
                            <Checkbox
                              label="SMS Notifications"
                              {...form.getInputProps(`reminders.${index}.enableSms`, {
                                type: 'checkbox',
                              })}
                            />
                          </Group>

                          {form.values.reminders[index].enableEmail && (
                            <>
                              <TextInput
                                label="Subject"
                                className="max-w-[1000px]"
                                placeholder="Enter email subject"
                                {...form.getInputProps(`reminders.${index}.subject`)}
                                required
                                error={form.errors[`reminders.${index}.subject`]}
                              />
                              <div className="item-start flex w-full gap-3">
                                <Textarea
                                  label="Email Template"
                                  placeholder="Enter email template..."
                                  autoComplete="off"
                                  resize="vertical"
                                  rows={
                                    reminder.emailTemplate.length
                                      ? reminder.emailTemplate.split('\n').length
                                      : 5
                                  }
                                  className="w-full max-w-[1000px]"
                                  {...form.getInputProps(`reminders.${index}.emailTemplate`)}
                                  error={form.errors[`reminders.${index}.emailTemplate`]}
                                  required
                                />
                                <Button
                                  className="mt-6"
                                  onClick={() => showPreview(index)}
                                  variant="light"
                                >
                                  Preview
                                </Button>
                              </div>
                            </>
                          )}

                          {form.values.reminders[index].enableSms && (
                            <Textarea
                              label="SMS Template"
                              placeholder="Enter SMS template..."
                              rows={3}
                              {...form.getInputProps(`reminders.${index}.smsTemplate`)}
                              error={form.errors[`reminders.${index}.smsTemplate`]}
                              required
                            />
                          )}
                        </Stack>
                      </Card>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>

              <Divider />

              <Group justify="space-between">
                <Button
                  leftSection={<IconPlus size="1rem" />}
                  variant="outline"
                  onClick={addReminder}
                  disabled={isLoading}
                >
                  Add Another Reminder
                </Button>

                <Button type="submit" loading={isLoading} disabled={!form.isValid() || isLoading}>
                  Save Reminders
                </Button>
              </Group>
            </Stack>
          </form>
        )}
      </div>
    </>
  )
}
